"use strict";
/**
 * Places markup in the head of an App page and marks its `<html>`, the same
 * way on the platform and in the StreamKit. A page may leave out `<html>` and
 * `<head>`, as HTML allows: the missing tag is written where the parser would
 * have put it, after the doctype, so the page keeps its standards mode.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.insertIntoAppHead = insertIntoAppHead;
exports.setAppHtmlAttribute = setAppHtmlAttribute;
exports.appHtmlElements = appHtmlElements;
// A tag name ends at whitespace, `/` or `>`; `\b` would also take `<html-x>`.
const HEAD_TAG = /<head(?=[\s/>])[^>]*>/i;
const HTML_TAG = /<html(?=[\s/>])([^>]*)>/i;
// Comments may stand before the doctype without changing the mode.
const DOCTYPE = /^(?:\s|<!--[\s\S]*?-->)*<!doctype\b[^>]*>/i;
/** One attribute value as HTML writes it: double, single or not quoted. */
const ATTRIBUTE_VALUE = String.raw `(?:"[^"]*"|'[^']*'|[^\s"'=<>\x60]+)`;
const insertAt = (html, at, markup) => `${html.slice(0, at)}${markup}${html.slice(at)}`;
/** Where a tag of the document element may go: after the doctype, if any. */
const afterDoctype = (html) => DOCTYPE.exec(html)?.[0].length ?? 0;
/** Puts `markup` first into the head of the page. */
function insertIntoAppHead(html, markup) {
    const head = HEAD_TAG.exec(html);
    if (head)
        return insertAt(html, head.index + head[0].length, markup);
    const root = HTML_TAG.exec(html);
    const at = root ? root.index + root[0].length : afterDoctype(html);
    return insertAt(html, at, `<head>${markup}</head>`);
}
/**
 * Sets one attribute on `<html>` and drops the value it had. `value` is
 * written as given, so it must already be escaped for an attribute.
 */
function setAppHtmlAttribute(html, name, value) {
    const existing = new RegExp(String.raw `\s+${name}(?:\s*=\s*${ATTRIBUTE_VALUE})?(?=[\s/>]|$)`, 'gi');
    if (!HTML_TAG.test(html))
        return insertAt(html, afterDoctype(html), `<html ${name}="${value}">`);
    return html.replace(HTML_TAG, (_match, attributes) => `<html${attributes.replace(existing, '')} ${name}="${value}">`);
}
/** Static opening tags; comments and raw/inert element contents are not markup. */
function* appHtmlElements(html) {
    const tokens = /<!--[\s\S]*?-->|<(script|style|textarea|title|template)\b(?:[^"'<>]|"[^"]*"|'[^']*')*>[\s\S]*?<\/\1\s*>|<[a-z][^\s/>]*(?:[^"'<>]|"[^"]*"|'[^']*')*>/gi;
    for (const token of html.matchAll(tokens)) {
        const tag = /^<([a-z][^\s/>]*)((?:[^"'<>]|"[^"]*"|'[^']*')*)>/i.exec(token[0]);
        if (!tag)
            continue;
        const attributes = new Map();
        const pattern = /([^\s"'<>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
        for (const match of tag[2].matchAll(pattern))
            attributes.set(match[1].toLowerCase(), match[2] ?? match[3] ?? match[4] ?? '');
        yield { tagName: tag[1].toLowerCase(), attributes };
    }
}
